import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, Typography, Paper, Box, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import Serial from '../modules/Serial';
import AutoUpdater from '../modules/AutoUpdater';
import { v4 as uuidv4 } from 'uuid';

const RESERVED_UUID = "uFFFFFF";

const BoardTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [stepMessages, setStepMessages] = useState<string[]>([]);
  const [boardUUID, setBoardUUID] = useState<string>(RESERVED_UUID);

  const [serial, setSerial] = useState<Serial | null>(null);
  const [step, setStep] = useState(1);
  const [showUpdater, setShowUpdater] = useState(false);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false); // Controls board number prompt dialog
  const firmwareUrl = 'https://db-board-tester.vercel.app/bin/ArdCode.ino.with_bootloader.hex';

  const [uniqueVerifiedUUID, setUniqueVerifiedUUID] = useState<string | null>(null);

  const lotNumber = ("UIDFA24"); // Default lot number is "UIDFA24"
  const [uniqueSerial, setUniqueSerial] = useState<string>("0001"); // Default board number is "0001"

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetDialogMessage, setResetDialogMessage] = useState<string | null>(null);

  useEffect(() => {
    const serialInstance = new Serial();
    setSerial(serialInstance);

    serialInstance.onSuccess = () => setMessage('Board connected successfully.');
    serialInstance.onFail = (errorMsg) => {
      setMessage(`Error: ${errorMsg}`);
      setLoading(false);
      logError(11); // Error code for failed initial connection
    };

    return () => {
      serialInstance.close();
    };
  }, []);

  const makeFullSerialNumber = (num1: string, num2: string): string => {
    return `${num1}-${num2}`;
  }

  const checkAndIncrement = (serialNum: string) => {
    let num = parseInt(serialNum);
    if (isNaN(num))
      return;
    else {
      num++;
      setUniqueSerial(num.toString().padStart(4, '0'));
    }
  }

  const errorMessages = {
    0: 'Valid Digilab One board with UUID',
    1: 'Failed to initially connect to the board',
    2: 'Failed to get firmware from URL',
    3: 'Failed to flash the board',
    4: 'Failed to reconnect to the board after flashing',
    5: 'Failed to generate a unique UUID not in the DB',
    6: 'The board already has a UUID that is not valid',
    7: 'The UUID flashing failed',
    8: 'Failed to save board to DB',
    9: 'Failed to save test result to DB',
    10: 'Failed to query for UUID from DB',
    11: 'Generally failed to connect'
  };

  // Log error by saving test results with error code
  const logError = async (errorCode: keyof typeof errorMessages) => {
    try {
      const fullSerial = makeFullSerialNumber(lotNumber, uniqueSerial);
      const errorMessage = errorMessages[errorCode] || 'Unknown error';
      console.log(`Logging error with code ${errorCode} for UUID ${RESERVED_UUID} and board# ${fullSerial}`);

      if (errorCode === 11) return; // Do not save the error if it is a general connection error

      // Display the error message in the stepMessages
      setStepMessages((prev) => [...prev, `Error ${errorCode}: ${errorMessage}`]);

      await fetch('/api/saveTestResult', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uuid: RESERVED_UUID,
          board_number: fullSerial,
          result: errorCode,
          timestamp: new Date().toISOString(),
        }),
      });

      resetTestWindow(2000, errorMessage); // Reset the window with error message
    } catch (error) {
      console.error('Failed to log error:', error);
      setStepMessages((prev) => [...prev, 'Error: Failed to log error to server']);
    }
  };

  // Generate a unique UUID by checking the database
  const generateUniqueUUID = async (): Promise<string> => {
    let newUUID;
    do {
      newUUID = `u${uuidv4().slice(0, 6)}`;
      console.log(`Generated UUID: ${newUUID}, checking uniqueness...`);
      const response = await fetch(`/api/checkBoard?uuid=${newUUID}`);
      const data = await response.json();
      if (!data.exists) break;
    } while (true);
    return newUUID;
  };

  // Handle AutoUpdater completion
  const handleUpdateComplete = (success: boolean) => {
    setShowUpdater(false); // Hide the updater dialog
    setLoading(false);
    if (success) {
      setStepMessages((prev) => [...prev, "Step 1 - PASSED - Firmware flashed successfully"]);
      setStep(2);
    } else {
      setMessage('Failed to flash firmware.');
    }
  };

  // Reset function to clear the window and prepare for the next test
  const resetTestWindow = (delay = 0, finalMessage: string | null = null) => {
    // Open the dialog with the final message
    setResetDialogMessage(finalMessage);
    setResetDialogOpen(true);

    setTimeout(() => {
      // Close the dialog after delay
      setResetDialogOpen(false);

      // Reset the state variables
      setMessage(null);
      setStepMessages([]);
      setBoardUUID(RESERVED_UUID);  // Reset UUID to default
      checkAndIncrement(uniqueSerial); // Increment the board number
      setStep(1);                   // Reset to the first step
      setBoardDialogOpen(false);    // Close any open dialogs
      if (serial) serial.close();   // Close the serial connection
      console.log("Test window reset for the next test.");
    }, delay);
  };

  const flashAndVerifyUUID = async () => {
    if (!serial) return;
    setLoading(true);
    setMessage('Flashing and verifying UUID...');

    try {
      const success = await serial.requestPort();
      if (!success) {
        logError(4);
        throw new Error('Failed to reconnect to the board after flashing');
      }

      // Generate a new unique UUID
      const uniqueUUID = await generateUniqueUUID();
      setBoardUUID(uniqueUUID);

      // Wait for the board to be ready to receive UUID
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Flash the UUID to the board
      serial.send(`${uniqueUUID}\r\n`);
      console.log(`[SERIAL] Sent UUID to board: ${uniqueUUID}`);

      // Wait for board to process UUID and send verification request
      await new Promise((resolve) => setTimeout(resolve, 4500));
      serial.send(`$\r\n`);
      console.log(`[SERIAL] Sent verification command: "$"`);

      // Await the board's response for UUID verification
      const uuidVerificationResult = await new Promise((resolve) => {
        serial.onReceive = (response) => {
          console.log(`[SERIAL] Received response from board: ${JSON.stringify(response)}`);

          if (response.type === "uuid" && response.data === uniqueUUID) {
            resolve("UUID verified");
          } else if (response.type === "uuid_not_set") {
            resolve("No UUID found");
          } else if (response.type === "uuid_exists" || response.type === "uuid") {
            resolve(response.data);
          } else {
            resolve("Unexpected response");
          }
        };

        // Timeout after 5 seconds if no response
        setTimeout(() => resolve("Timeout"), 5000);
      });

      // Handle verification result
      if (uuidVerificationResult === "UUID verified") {
        // UUID verified, save to database
        setUniqueVerifiedUUID(uniqueUUID);
        await saveBoardToDatabase(uniqueUUID);
        await saveTestResultToDatabase(uniqueUUID, "0", "Valid Digilab One board with UUID");
        setStepMessages((prev) => [...prev, "Step 2 - PASSED - UUID flashed and verified"]);
        resetTestWindow(4000, 'Test passed successfully. Resetting...');
      } else if (uuidVerificationResult === "No UUID found") {
        console.error('Board did not store UUID.');
        logError(7);
      } else if (uuidVerificationResult && uuidVerificationResult !== uniqueUUID) {
        // If a different UUID is received, check if it exists in the database
        console.log(`[SERIAL] Received mismatched UUID from board: ${uuidVerificationResult}`);

        const existingUUID = await checkUUIDInDatabase(String(uuidVerificationResult));
        if (existingUUID) {
          console.log(`[SERIAL] UUID found in database: ${uuidVerificationResult}. Associating test with existing UUID.`);
          setUniqueVerifiedUUID(existingUUID);
          await saveTestResultToDatabase(String(uuidVerificationResult), "0", "Valid Digilab One board with existing UUID");
          setStepMessages((prev) => [...prev, "Step 2 - PASSED - Existing UUID verified"]);
          resetTestWindow(4000, 'Test passed with existing UUID. Resetting...');
        } else {
          console.error('Unexpected UUID received, and it is not in the database.');
          logError(6);
        }
      } else {
        console.error('Unexpected response during UUID verification:', uuidVerificationResult);
        logError(7);
      }
    } catch (error) {
      console.error('Error during UUID flashing and verification:', error);
      setMessage('Error during UUID flashing and verification');
      logError(7);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if a UUID exists in the database
  const checkUUIDInDatabase = async (uuid: string): Promise<string | null> => {
    try {
      const response = await fetch(`/api/checkBoard?uuid=${uuid}`);
      const data = await response.json();
      return data.exists ? uuid : null;
    } catch (error) {
      console.error('Failed to check UUID in database:', error);
      logError(10); // failed to check UUID in database
      return null;
    }
  };

  // Updated saveBoardToDatabase to accept UUID as a parameter
  const saveBoardToDatabase = async (uuid: string) => {
    if (!uniqueSerial || !uuid) return;
    try {
      const fullSerial = makeFullSerialNumber(lotNumber, uniqueSerial);
      console.log(`Saving board to DB with UUID: ${uuid}, Board Number: ${fullSerial}`);
      await fetch('/api/saveBoard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uuid: uuid,
          board_number: fullSerial,
          vendor_id: serial?.getInfo().usbVendorId,
          product_id: serial?.getInfo().usbProductId,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error('Failed to save board:', error);
      logError(8);
    }
  };

  // Updated saveTestResultToDatabase to accept UUID as a parameter
  const saveTestResultToDatabase = async (uuid: string, errorCode: string, message: string) => {
    if (!uuid) return;
    if (errorCode === "0") return; // Do not save successful test results as they are already saved in the board DB
    try {
      const fullSerial = makeFullSerialNumber(lotNumber, uniqueSerial);
      console.log(`Saving test result for UUID: ${uuid}, Result: ${errorCode}, Serial: ${fullSerial}`);
      await fetch('/api/saveTestResult', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uuid: uuid,
          board_number: fullSerial,
          result: errorCode,
          timestamp: new Date().toISOString(),
        }),
      });

      // Indicate success or failure in the info display
      const finalMessage = errorCode === "0" ? "Test passed successfully. Resetting..." : "Test failed. Retrying in 2 seconds...";
      resetTestWindow(4000, finalMessage); // Wait 2 seconds to reset
    } catch (error) {
      console.error('Failed to save test result:', error);
      setMessage("Error saving test result. Retrying in 2 seconds...");
      logError(9);  // failed to save test result
    }
  };

  // Adjust flashFirmware to open the board number dialog first
  const flashFirmware = () => {
    // Open the dialog to enter the board number
    setBoardDialogOpen(true);
  };

  // Add a function to handle board number confirmation and initiate the firmware update
  const handleBoardNumberConfirm = () => {
    // Close the dialog and start firmware flashing
    setBoardDialogOpen(false);
    setShowUpdater(true); // Display the AutoUpdater dialog to begin firmware flashing
  };

  return (
    <Paper elevation={0} sx={{ p: 3, background: '#333' }}>
      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h4">Digilab Board Tester</Typography>
          <Typography variant="subtitle1">
            Follow the steps to connect and flash the firmware, then verify the UUID.
          </Typography>
        </Paper>

        <Paper elevation={2} sx={{ p: 2, m: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          {loading ? (
            <>
              <CircularProgress />
              <Typography>{message}</Typography>
            </>
          ) : (
            <>
              <Button variant="contained" onClick={flashFirmware} disabled={step !== 1} sx={{ mb: 1 }}>
                Step 1: Connect & Flash Firmware
              </Button>
              <Button variant="contained" onClick={flashAndVerifyUUID} disabled={step !== 2} sx={{ mb: 1 }}>
                Step 2: Connect & Flash/Verify UUID
              </Button>
            </>
          )}
        </Paper>

        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6">Test Progress:</Typography>
          {stepMessages.map((msg, index) => (
            <Typography key={index} sx={{ mt: 1, color: msg.includes("PASSED") ? "green" : "red" }}>
              {msg}
            </Typography>
          ))}
        </Paper>

        {/* AutoUpdater component for firmware flashing */}
        {showUpdater && (
          <AutoUpdater
            serial={serial!}
            firmwareUrl={firmwareUrl}
            latestVersion="3v4"
            onComplete={handleUpdateComplete}
            logError={logError}
          />
        )}

        {/* Dialog to prompt for board number */}
        <Dialog open={boardDialogOpen} onClose={() => setBoardDialogOpen(false)}>
          <DialogTitle>Enter Board Number</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ mr: 2 }}>{lotNumber}</Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Serial Number"
                type="text"
                fullWidth
                variant="outlined"
                value={uniqueSerial}
                onChange={(e) => setUniqueSerial(e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBoardDialogOpen(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleBoardNumberConfirm} color="primary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Dialog to display the final message */}
        <Dialog open={resetDialogOpen}>
          <DialogTitle>Test Result</DialogTitle>
          <DialogContent>
          <Typography>Test Ended, will resest soon</Typography>
            <Typography>{resetDialogMessage}</Typography>
          </DialogContent>
        </Dialog>
      </Box>
    </Paper>
  );
};

export default BoardTest;
